import React from "react";
import { NavLink, Outlet, Navigate, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Gauge, Users, ToggleRight, ClipboardList, Activity, LogOut, Sparkles, Users2, Trophy,
} from "lucide-react";
import { adminApi, adminAuth } from "@/admin/api";
import { cn } from "@/lib/utils";

const nav = [
  { to: "/admin", icon: Gauge, label: "Dashboard", end: true },
  { to: "/admin/users", icon: Users, label: "App Users" },
  { to: "/admin/features", icon: ToggleRight, label: "Feature Flags" },
  { to: "/admin/set-pieces", icon: Trophy, label: "Set Pieces" },
  { to: "/admin/lineups", icon: Users2, label: "Predicted Lineups" },
  { to: "/admin/audit", icon: ClipboardList, label: "Audit Log" },
  { to: "/admin/health", icon: Activity, label: "System Health" },
];

export default function AdminLayout() {
  const nav_ = useNavigate();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin-me"],
    queryFn: async () => (await adminApi.get("/me")).data,
    enabled: adminAuth.isLoggedIn(),
    retry: 0,
  });

  if (!adminAuth.isLoggedIn()) return <Navigate to="/admin/login" replace />;
  if (isError) {
    adminAuth.clear();
    return <Navigate to="/admin/login" replace />;
  }

  const admin = data?.admin;

  return (
    <div className="min-h-screen bg-totem-bg text-totem-text flex">
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-60 shrink-0 border-r border-totem-border bg-totem-surface">
        <div className="p-5 border-b border-totem-border flex items-center gap-2">
          <div className="grid place-items-center w-8 h-8 rounded-lg bg-totem-green">
            <Sparkles size={16} className="text-totem-bg" strokeWidth={2.5} />
          </div>
          <div>
            <div className="mono uppercase text-[9px] tracking-[0.2em] text-totem-textDim">FPL</div>
            <div className="display-heading text-sm text-totem-green -mt-0.5">TOTEM ADMIN</div>
          </div>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1">
          {nav.map(({ to, icon: Icon, label, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition",
                  isActive
                    ? "bg-totem-green/10 text-totem-green border border-totem-green/30"
                    : "text-totem-textDim hover:text-totem-text hover:bg-totem-surfaceHigh"
                )
              }
            >
              <Icon size={16} strokeWidth={1.8} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-totem-border">
          <div className="text-xs font-semibold truncate">{admin?.email || "…"}</div>
          <div className="mono text-[10px] uppercase text-totem-textDim mt-0.5">
            {admin?.role || ""}
          </div>
          <button
            onClick={() => { adminAuth.clear(); nav_("/admin/login"); }}
            className="mt-3 w-full flex items-center justify-center gap-1.5 py-1.5 rounded-md border border-totem-border hover:border-totem-red/60 text-xs mono uppercase text-totem-textDim hover:text-totem-red transition"
          >
            <LogOut size={12} /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-30 bg-totem-surface border-b border-totem-border">
        <div className="px-4 py-2 flex items-center justify-between">
          <div className="display-heading text-totem-green text-sm">TOTEM ADMIN</div>
          <button
            onClick={() => { adminAuth.clear(); nav_("/admin/login"); }}
            className="text-totem-textDim"
            aria-label="Sign out"
          >
            <LogOut size={16} />
          </button>
        </div>
        <div className="flex overflow-x-auto gap-1 px-2 pb-2">
          {nav.map(({ to, icon: Icon, label, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                cn(
                  "shrink-0 flex items-center gap-1 px-3 py-1 rounded text-[11px] mono uppercase tracking-wider transition",
                  isActive ? "bg-totem-green text-totem-bg" : "bg-totem-surfaceElev text-totem-textDim"
                )
              }
            >
              <Icon size={12} /> {label}
            </NavLink>
          ))}
        </div>
      </div>

      <main className="flex-1 min-w-0 pt-24 md:pt-0">
        <div className="max-w-6xl mx-auto p-6">
          {isLoading ? (
            <div className="h-64 animate-pulse totem-card" />
          ) : (
            <Outlet context={{ admin }} />
          )}
        </div>
      </main>
    </div>
  );
}
